<template>
    <div class="tables">

        <v-tabs-items
            ref="tabsContainer"
            v-model="currentTab"
            class="tabs-items"
        >
            <v-tab-item
                v-for="(sector, index) in sectors"
                :key="sector.id"
                :value="`tab-${index}`"
                :transition="tabTransition"
            >
                <div
                    class="tab-container"
                    :style="{ backgroundImage: `url(${sector.image_url})` }"
                >
                    <table-button
                        v-for="table in sector.tables"
                        :id="'table-' + table.table.id"
                        :key="table.table.id"
                        v-resize="onResize"
                        :table="table.table"
                        :table-permissions="tablePermissions"
                        :badge="getTableBadge(table)?.icon"
                        :badge-color="getTableBadge(table)?.color"
                        :disabled="isDisabled(table)"
                        :parent-height="tableContainer.height"
                        :parent-width="tableContainer.width"
                        :orders="table.orders"
                        @long-press="acknowledgeKitchenTickets(table)"
                        @click="onTableSelect(table)"
                    />
                </div>
            </v-tab-item>
        </v-tabs-items>

        <!-- Select User Dialog -->
        <select-user
            :message="$t('tables.hand_over_table_message')"
            :show.sync="show.selectUser"
            @select="onUserSelect"
        />

        <v-alert
            v-if="alertMsg"
            class="tables-alert"
            type="warning"
            text
            transition="slide-y-transition"
        >
            {{ alertMsg }}
        </v-alert>

        <v-alert
            v-else-if="certExpiration"
            class="order-alert"
            type="error"
            transition="slide-y-transition"
        >
            {{ certExpiration.msg }}
            <v-btn
                v-if="certExpiration.addNew"
                class="ml-2"
                rounded
                outlined
                @click="$router.push({ name: 'adminAccount' }).catch(err => {})"
            >
                {{ $t('general.add_new') }}
            </v-btn>
        </v-alert>

    </div>
</template>

<script>
import TableButton from '@/components/TableButton';
import SelectUser from '@/views/shared/components/SelectUser';

import User from '@/models/User';
import Sector from '@/models/Sector';
import Order from '@/models/Order';
import Product from '@/models/Product';
import Role from '@/models/Role';
import Table from '@/models/Table';
import Shift from '@/models/Shift';
import CashRegister from '@/models/CashRegister';
import Notification from '@/models/Notification';
import KitchenTicket from '@/models/KitchenTicket';

import FiscalService from '@/services/FiscalService';
import UserService from '@/services/UserService';
import ApiService from '@/services/ApiService';
import { mapGetters } from 'vuex';
import * as mutationTypes from '@/store/mutationTypes';
import moment from 'moment-timezone';

export default {
    name: 'Tables',

    components: {
        TableButton,
        SelectUser
    },

    async created() {
        this.eventBus.$on('tables-show-tab', this.showTab)
        this.eventBus.$on('orders-refresh', this.loadData)
        this.eventBus.$on('orders-updated', this.getSectors)
        this.eventBus.$on('kitchen-tickets-sync', this.getSectors)
        this.eventBus.$on('issue-receipt', this.createNewReceipt)

        // activated
        setTimeout(() => this.onResize(), 100);

        this.getSectors();
        this.loadData();
        this.checkNotifications();

        this.sdcUiData = (await FiscalService.getUiData('ESDC')).data || null
    },

    mounted() {
        setTimeout(() => (this.tabTransition = 'v-window-x-reverse-transition'), 1000);

        if(this.curShift && this.curCashRegister && this.curCashRegister.depositRequired()) {
            this.eventBus.$emit('cash-register-deposit')
        }
    },

    beforeDestroy() {
        this.eventBus.$off('tables-show-tab', this.showTab)
        this.eventBus.$off('orders-refresh', this.loadData)
        this.eventBus.$off('orders-updated', this.getSectors)
        this.eventBus.$off('kitchen-tickets-sync', this.getSectors)
        this.eventBus.$off('issue-receipt', this.createNewReceipt)
    },

    data: () => ({
        sectors: [],
        currentTab: 'tab-0',
        tabTransition: false,
        tableToTransfer: null,

        tableContainer: {
            height: 0,
            width: 0
        },

        show: {
            selectUser: false
        },

        sdcUiData: null
    }),

    watch: {
        tableMode(val) {
            this.tableToTransfer = null;

            if(val === 'handOverShift') {
                this.show.selectUser = true;
            }
        },

        'show.selectUser'(val) {
            if(!val) {
                this.setTableMode(null);
            }
        }
    },

    computed: {
        ...mapGetters(['trainingMode', 'tableMode', 'tablePermissions']),

        curShift() {
            return Shift.current();
        },

        curCashRegister() {
            return CashRegister.current();
        },

        alertMsg() {
            if(this.trainingMode) {
                return this.$t('general.training_active')
            }

            return null
        },

        certExpiration() {
            if(this.tenant?.cert_expire_at && moment(this.tenant.cert_expire_at).diff(moment(), 'days') < 30) {
                return {
                    addNew: this.allows('nav_access_admin'),
                    msg: this.$t('general.certificate_expires', { value: moment(this.tenant.cert_expire_at).format('DD.MM.YYYY.') })
                }
            }

            if(!FiscalService.useDevEsdc && this.sdcUiData?.certExpDate && moment(this.sdcUiData.certExpDate).diff(moment(), 'days') < 30) {
                const expired = moment(this.sdcUiData.certExpDate).diff(moment(), 'days') <= 0
                return {
                    addNew: false,
                    msg: this.$t(
                        expired ? 'sdc.esdc.certificate_expired_message' : 'sdc.esdc.certificate_expires_message',
                        { value: moment(this.sdcUiData.certExpDate).format('DD.MM.YYYY.') }
                    )
                }
            }

            return null
        },
    },

    methods: {
        async loadData() {
            if (!this.isOnline) return;

            const ordersLastUpdatedAt = Order.getLastUpdatedAt();
            const productsLastUpdatedAt = Product.getLastUpdatedAt();
            const ordersOnTables = Order.onTables();
            const kitchenTickets = KitchenTicket.all().filter(kt => kt.isDone() && !kt.isAcknowledged());

            const data = {
                orders_last_updated_at: ordersLastUpdatedAt ? ordersLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null,
                products_last_updated_at: productsLastUpdatedAt ? productsLastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null,
                refresh_order_ids: ordersOnTables.filter(o => !!o.id).map(o => o.id),
                refresh_kitchen_ticket_ids: kitchenTickets.filter(kt => !!kt.id).map(kt => kt.id),
            }

            try {
                const response = await ApiService.post('/load/tables', data);

                ordersOnTables.forEach(order => {
                    const orderDeletedOnServer = response.data.deleted_order_ids?.some(id => id == order.id);
                    if(orderDeletedOnServer) {
                        order.$delete();
                    }
                })

                Order.insert({ data: response.data.orders, freeze: true });
                Product.insert({ data: response.data.products, freeze: true });
                Shift.insert({ data: [ response.data.shift ] });
                Sector.create({ data: response.data.sectors });
                Table.create({ data: response.data.tables });
                // User.insert({ data: response.data.users });
                // Role.insert({ data: response.data.roles });

                if(response.data.kitchen_tickets?.length) {
                    response.data.kitchen_tickets.forEach(kt => {
                        const storedKitchenTicket = kitchenTickets.find(k => k.id === kt.id)
                        if(moment(storedKitchenTicket.updated_at).isBefore(moment(kt.updated_at))) {
                            KitchenTicket.update(kt)
                        }
                    })
                }

                this.getSectors();
            } catch(error) {
                console.log(error.message);
            }
        },

        getSectors() {
            const orders = Order.onTables();
            const tables = Table.all();
            const kitchenTickets = KitchenTicket.all().filter(kt => kt.isDone() && !kt.isAcknowledged());

            this.sectors = Sector.all().map(sector => {
                sector.tables = tables.filter(table => table.sector_id === sector.id).map(table => {
                    return {
                        table: table,
                        orders: orders.filter(order => order.table_id === table.id),
                        kitchenKitcketReady: kitchenTickets.some(kt => kt.table_id === table.id),
                    }
                })

                return sector
            })

            const freeTables = this.sectors.flatMap(sector => sector.tables).filter(table => table.orders.length === 0);
            freeTables.forEach(table => this.transferTableToUser(table.table, null))
        },

        createNewReceipt(order) {
            this.$store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: order });
            this.goToOrder();
        },

        onResize() {
            if(this.$refs.tabsContainer) {
                this.tableContainer.width = this.$refs.tabsContainer.$el.offsetWidth;
                this.tableContainer.height = this.$refs.tabsContainer.$el.offsetHeight;
            }
        },

        onTableSelect(table) {
            if(this.tableMode) {
                if(this.tableToTransfer) {
                    this.$confirm(
                        table.orders.length
                            ? this.$t('tables.transfer_warning_occupied_confirm')
                            : this.$t('tables.transfer_warning_confirm'),
                        {
                            buttonTrueText: this.$t('general.confirm'),
                            buttonCancelText: this.$t('general.cancel'),
                            icon: 'fal fa-times',
                            color: 'error',
                            title: this.$t('tables.transfer'),
                            persistent: true,
                        },
                        async result => {
                            if(result) {
                                await this.transferTable(this.tableToTransfer, table.table);
                                this.getSectors();
                            }

                            this.setTableMode(null);
                        }
                    );
                    return;
                }

                if(this.tableMode === 'handOverTable') {
                    this.show.selectUser = true;
                }

                this.tableToTransfer = table.table;
                return;
            }

            this.goToOrder(table);
        },

        acknowledgeKitchenTickets(table) {
            KitchenTicket.all()
                .filter(kt => kt.isDone() && !kt.isAcknowledged() && kt.table_id == table.table.id)
                .forEach(kt => kt.acknowledge())

            this.getSectors()
        },

        onUserSelect(user) {
            if(this.tableMode === 'handOverTable') {
                this.transferTableToUser(this.tableToTransfer, user);
                this.getSectors();
            }

            if(this.tableMode === 'handOverShift') {
                const tables = this.sectors
                    .map(sector => sector.tables)
                    .flat()
                    .filter(table => {
                        const lastOrder = table.orders.length > 0 ? table.orders[table.orders.length - 1] : null;
                        if(!lastOrder || !this.user) return false;

                        if(this.tablePermissions[table.table.id]) {
                            return this.tablePermissions[table.table.id] === this.user.id;
                        }

                        return lastOrder.user_id === this.user.id;
                    })
                    .map(table => table.table);

                tables.forEach(table => {
                    this.transferTableToUser(table, user);
                })

                UserService.clockOut();
                this.$router.push({ name: 'userLogin' }).catch(err => {});
            }
        },

        goToOrder(table = null) {
            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            if (!table) {
                this.$router.push('order').catch(err => {});
                return;
            }

            if(!this.canAccessTable(table.table, table.orders)) {
                this.$snackbar(this.$t('tables.table_access_not_allowed'), 'error');
                return;
            }

            this.$router.push({
                name: 'order',
                params: { table_id: table.table.id }
            }).catch(err => {});
        },

        canAccessTable(table, orders) {
            if(!orders.length) return true;

            if(this.allows('allow_enter_all_tables')) return true;

            const curUser = new User(this.user);
            return curUser.getTables().some(t => t.id === table.id);
        },

        isDisabled(table) {
            if(this.tableMode && !this.tableToTransfer) {
                if(!table.orders.length || !this.canAccessTable(table.table, table.orders)) {
                    return true;
                }
            }

            return !this.contractAllows('table_plan') || !this.curShift;
        },

        setTableMode(mode = null) {
            this.$store.commit(mutationTypes.SET_TABLE_MODE, { tableMode: mode });
        },

        async transferTable(srcTable, dstTable) {
            if(!srcTable || !dstTable) return;

            await srcTable.transferToTable(dstTable);
        },

        transferTableToUser(srcTable, user) {
            if(!srcTable) return;

            const tablePermissions = this.tablePermissions;
            if(!user) {
                if(!tablePermissions[srcTable.id]) return;

                delete tablePermissions[srcTable.id];
            } else {
                tablePermissions[srcTable.id] = user.id;
            }

            this.$store.commit(mutationTypes.SET_TABLE_PERMISSIONS, { tablePermissions });
        },

        getTableBadge(tableData) {
            if(!!this.tableToTransfer && this.tableToTransfer.id === tableData.table.id) {
                return { icon: 'fas fa-chevron-right', color: 'error' }
            } else if(tableData.kitchenKitcketReady) {
                return { icon: 'fas fa-check', color: 'success' }
            }

            return { icon: null, color: null }
        },

        showTab(tab) {
            this.currentTab = tab
        },

        async checkNotifications() {
            await Notification.fetchUnread()
            const unreadNotifications = Notification.unread()
            if(unreadNotifications.length) {
                this.eventBus.$emit('show-notification', { notification: unreadNotifications[0] });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.tables {
    height: 100%;
    width: 100%;
    padding: 0;

    .tabs-items {
        height: 100%;
        width: 100%;

        ::v-deep .v-window__container {
            height: 100%;

            .v-window-item {
                height: 100%;
            }
        }

        .tab-container {
            height: 100%;
            background-size: 100% 100%;
        }
    }

    .tables-alert {
        position: fixed;
        bottom: 78px;
        left: 15px;
        margin-right: 15px;
        z-index: 5;
    }
}
</style>
